// @ts-check
/**
 * Shipping Partners — the carrier registry.
 *
 * This page is the only place carriers and their tracking URLs are configured. The
 * tracking URL pattern uses {awb} as the placeholder, so a tracking link can be built
 * for any AWB without touching code.
 */

import { Edit2, MapPin, Package, Plug, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import FileUpload from '../components/FileUpload';
import LoadingScreen from '../components/LoadingScreen';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import { useRegisterUnsavedChanges } from '../context/UnsavedChangesContext';
import adminShippingService from '../services/adminShippingService';
import adminSiteSettingsService from '../services/adminSiteSettingsService';

const PROVIDERS = [
  { value: 'manual', label: 'Manual (enter AWB by hand)' },
  { value: 'shiprocket', label: 'Shiprocket' },
  { value: 'shipglobal', label: 'ShipGlobal' },
];

const SELLER_ADDRESS_KEY = 'shipping_seller_address';
const SHIPROCKET_PICKUP_KEY = 'shiprocket_pickup_location';

// Mirrors the mandatory seller_* fields on ShipGlobal's addOrder.php.
const SELLER_FIELDS = [
  { key: 'company', label: 'Company Name', required: true },
  { key: 'nickname', label: 'Nickname / Warehouse Label', required: true },
  { key: 'firstname', label: 'Contact First Name', required: true },
  { key: 'lastname', label: 'Contact Last Name', required: true },
  { key: 'mobile', label: 'Mobile', required: true },
  { key: 'email', label: 'Email', required: true },
  { key: 'address', label: 'Address', required: true, wide: true },
  { key: 'city', label: 'City', required: true },
  { key: 'state', label: 'State', required: true },
  { key: 'postcode', label: 'Postcode', required: true },
  { key: 'country_code', label: 'Country Code', required: true },
  { key: 'tax_id_type', label: 'Tax ID Type', required: false },
  { key: 'tax_id', label: 'Tax ID (GSTIN)', required: false },
  { key: 'ioss_number', label: 'IOSS Number (EU shipments)', required: false },
];

const EMPTY_FORM = {
  name: '',
  code: '',
  logo_url: '',
  tracking_url_pattern: '',
  provider: 'manual',
  service_code: '',
  api_base_url: '',
  api_enabled: false,
  supports_domestic: true,
  supports_international: false,
  is_active: true,
  display_order: 0,
  notes: '',
};

const ShippingPartnersPage = () => {
  const toast = useToast();
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Touched-since-open flag, reset by openCreate/openEdit (loading a form
  // never itself counts as an edit) and after a successful save.
  const [isDirty, setIsDirty] = useState(false);
  const [form, setFormRaw] = useState(EMPTY_FORM);
  const setForm = useCallback((updater) => {
    setIsDirty(true);
    setFormRaw(updater);
  }, []);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // Same touched-since-load pattern for the separate, inline pickup/seller
  // address form below (not modal-based, so it registers directly).
  const [sellerDirty, setSellerDirty] = useState(false);
  useRegisterUnsavedChanges(sellerDirty);
  const [seller, setSellerRaw] = useState(null);
  const setSeller = useCallback((updater) => {
    setSellerDirty(true);
    setSellerRaw(updater);
  }, []);
  const [savingSeller, setSavingSeller] = useState(false);
  const [showSeller, setShowSeller] = useState(false);
  const [pickupLocation, setPickupLocation] = useState('');
  const [pickupLocations, setPickupLocations] = useState([]);
  const [loadingPickups, setLoadingPickups] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [carrierData, sellerData, pickupData] = await Promise.allSettled([
        adminShippingService.getCarriers(),
        adminSiteSettingsService.getSetting(SELLER_ADDRESS_KEY),
        adminSiteSettingsService.getSetting(SHIPROCKET_PICKUP_KEY),
      ]);

      if (pickupData.status === 'fulfilled') setPickupLocation(pickupData.value?.value || '');

      if (carrierData.status === 'fulfilled') setCarriers(carrierData.value || []);
      else toast.error('Failed to load shipping partners');

      // The setting is seeded by migration; if it's somehow absent, fall back to blanks
      // rather than leaving the section broken.
      if (sellerData.status === 'fulfilled') {
        try {
          setSellerRaw(JSON.parse(sellerData.value?.value || '{}'));
        } catch {
          setSellerRaw({});
        }
      } else {
        setSellerRaw({});
      }
      setSellerDirty(false);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const handleSaveSeller = async () => {
    setSavingSeller(true);
    try {
      await adminSiteSettingsService.updateSetting(SELLER_ADDRESS_KEY, {
        value: JSON.stringify(seller || {}),
      });
      setSellerDirty(false);
      toast.success('Pickup address saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save pickup address');
    } finally {
      setSavingSeller(false);
    }
  };

  const handleTestConnection = async () => {
    if (!editingId) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await adminShippingService.testConnection(editingId);
      setTestResult(result);
      // A successful test is the natural moment to load pickup locations — credentials
      // are known good, so a failure here is about the locations, not the auth.
      if (result.connected && form.provider === 'shiprocket') loadPickupLocations();
    } catch (err) {
      setTestResult({ connected: false, reason: err.message || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const loadPickupLocations = async () => {
    if (!editingId) return;
    setLoadingPickups(true);
    try {
      const result = await adminShippingService.getPickupLocations(editingId);
      setPickupLocations(result.locations || []);
      if (result.reason) toast.error(result.reason);
    } catch (err) {
      toast.error(err.message || 'Failed to load pickup locations');
    } finally {
      setLoadingPickups(false);
    }
  };

  const handleSavePickupLocation = async (value) => {
    setPickupLocation(value);
    try {
      await adminSiteSettingsService.updateSetting(SHIPROCKET_PICKUP_KEY, { value });
      toast.success('Pickup location saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save pickup location');
    }
  };

  const missingSellerFields = SELLER_FIELDS.filter((f) => f.required && !String(seller?.[f.key] || '').trim());

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setFormRaw(EMPTY_FORM);
    setIsDirty(false);
    setTestResult(null);
    setShowModal(true);
  };

  const openEdit = (carrier) => {
    setEditingId(carrier.id);
    setTestResult(null);
    setFormRaw({
      name: carrier.name || '',
      code: carrier.code || '',
      logo_url: carrier.logo_url || '',
      tracking_url_pattern: carrier.tracking_url_pattern || '',
      provider: carrier.provider || 'manual',
      service_code: carrier.service_code || '',
      api_base_url: carrier.api_base_url || '',
      api_enabled: !!carrier.api_enabled,
      supports_domestic: !!carrier.supports_domestic,
      supports_international: !!carrier.supports_international,
      is_active: !!carrier.is_active,
      display_order: carrier.display_order ?? 0,
      notes: carrier.notes || '',
    });
    setIsDirty(false);
    setShowModal(true);
  };

  // X/Cancel dismiss the modal — confirm first if there's anything to lose.
  // A successful save closes directly (see handleSave), bypassing this.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setShowModal(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Partner name is required');
      return;
    }
    if (form.tracking_url_pattern && !form.tracking_url_pattern.includes('{awb}')) {
      toast.error('Tracking URL pattern must contain {awb}');
      return;
    }

    // Derive a stable code from the name on create; never change it on edit, since
    // existing shipments and any future API adapter key off it.
    const payload = {
      ...form,
      code: editingId
        ? form.code
        : form.name
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, ''),
      tracking_url_pattern: form.tracking_url_pattern.trim() || null,
      logo_url: form.logo_url || null,
      notes: form.notes.trim() || null,
      service_code: form.service_code.trim() || null,
      api_base_url: form.api_base_url.trim() || null,
      display_order: Number(form.display_order) || 0,
    };

    setSaving(true);
    try {
      if (editingId) {
        await adminShippingService.updateCarrier(editingId, payload);
        toast.success('Shipping partner updated');
      } else {
        await adminShippingService.createCarrier(payload);
        toast.success('Shipping partner added');
      }
      setIsDirty(false);
      setShowModal(false);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to save shipping partner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (carrier) => {
    try {
      const result = await adminShippingService.deleteCarrier(carrier.id);
      toast.success(result.message);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to delete shipping partner');
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-serif flex items-center gap-2">
            <Package size={24} />
            Shipping Partners
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Carriers available when recording a shipment. The tracking URL pattern builds the customer&apos;s tracking
            link — use <code className="bg-muted px-1">{'{awb}'}</code> where the tracking number goes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2"
          >
            <Plus size={16} />
            Add Partner
          </button>
        </div>
      </div>

      {/* Pickup / seller address — one warehouse address sent to the carrier on every
          shipment. Required by the carrier APIs, so it's flagged when incomplete. */}
      <div className="bg-card border rounded-lg border-border mb-6">
        <button
          onClick={() => setShowSeller(!showSeller)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-muted/50"
        >
          <span className="flex items-center gap-2 font-medium">
            <MapPin size={18} />
            Pickup / Seller Address
            {missingSellerFields.length > 0 && (
              <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                {missingSellerFields.length} required field
                {missingSellerFields.length === 1 ? '' : 's'} missing
              </span>
            )}
          </span>
          <span className="text-sm text-muted-foreground/70">{showSeller ? 'Hide' : 'Edit'}</span>
        </button>

        {showSeller && (
          <div className="border-t border-border p-4">
            <p className="text-sm text-muted-foreground mb-4">
              Sent as the sender on every shipment created through a carrier API. The carrier rejects label requests
              when any required field is blank.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {SELLER_FIELDS.map((field) => (
                <div key={field.key} className={field.wide ? 'md:col-span-3' : ''}>
                  <label className="block text-sm font-medium mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500"> *</span>}
                  </label>
                  <input
                    type="text"
                    value={seller?.[field.key] || ''}
                    onChange={(e) => setSeller({ ...seller, [field.key]: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground text-sm"
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleSaveSeller}
                disabled={savingSeller}
                className="px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50"
              >
                {savingSeller ? 'Saving...' : 'Save Pickup Address'}
              </button>
            </div>
          </div>
        )}
      </div>

      {carriers.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border rounded-lg border-border">
          No shipping partners configured yet.
        </div>
      ) : (
        <div className="bg-card border rounded-lg border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Partner</th>
                <th className="text-left px-4 py-3 font-medium">Lanes</th>
                <th className="text-left px-4 py-3 font-medium">Tracking URL</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {carriers.map((carrier) => (
                <tr key={carrier.id} className="border-b border-border/60 last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {carrier.logo_url && (
                        <img src={carrier.logo_url} alt={carrier.name} className="h-6 w-auto object-contain" />
                      )}
                      <div>
                        <p className="font-medium">{carrier.name}</p>
                        <p className="text-xs text-muted-foreground/70 font-mono">{carrier.code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {carrier.supports_domestic && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300">
                          Domestic
                        </span>
                      )}
                      {carrier.supports_international && (
                        <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300">
                          International
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {carrier.tracking_url_pattern ? (
                      <span className="text-xs text-muted-foreground font-mono break-all">
                        {carrier.tracking_url_pattern}
                      </span>
                    ) : (
                      <span className="text-xs text-amber-700 dark:text-amber-300">
                        Not set — tracking link entered per shipment
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs px-2 py-0.5 ${
                        carrier.is_active
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {carrier.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(carrier)}
                        className="p-2 text-muted-foreground hover:text-foreground"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(carrier)}
                        className="p-2 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal maxWidth="2xl" closeOnBackdrop={false} onClose={handleCloseClick} hasUnsavedChanges={isDirty}>
          <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-serif">{editingId ? 'Edit Shipping Partner' : 'Add Shipping Partner'}</h2>
            <button onClick={handleCloseClick} className="text-muted-foreground hover:text-foreground">
              <X size={22} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Partner Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Blue Dart"
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Integration</label>
                <select
                  value={form.provider}
                  onChange={(e) => setForm({ ...form, provider: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                >
                  {PROVIDERS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* API configuration — only meaningful once an integration exists for the
                  selected provider. Credentials are intentionally absent: they live in
                  Railway env vars, not the database. */}
            {form.provider !== 'manual' && (
              <div className="border rounded-lg border-border p-4 space-y-3 bg-muted/50">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Plug size={16} />
                  API Configuration
                </p>

                {/* Gates the "Create with <carrier>" action on the order screen. Off by
                      default so a half-configured carrier can't push a real shipment —
                      turn it on only once Test Connection passes. */}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.api_enabled}
                    onChange={(e) => setForm({ ...form, api_enabled: e.target.checked })}
                  />
                  Create shipments through this carrier&apos;s API
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Service Code</label>
                    <input
                      type="text"
                      value={form.service_code}
                      onChange={(e) => setForm({ ...form, service_code: e.target.value })}
                      placeholder={
                        form.provider === 'shipglobal'
                          ? 'Not used — ShipGlobal service is chosen per shipment from a live rate lookup'
                          : ''
                      }
                      disabled={form.provider === 'shipglobal'}
                      className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground disabled:bg-muted disabled:text-muted-foreground/70"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">API Base URL</label>
                    <input
                      type="text"
                      value={form.api_base_url}
                      onChange={(e) => setForm({ ...form, api_base_url: e.target.value })}
                      placeholder={
                        form.provider === 'shipglobal'
                          ? 'https://app.shipglobal.in/apiv1/'
                          : 'https://apiv2.shiprocket.in'
                      }
                      className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Shiprocket's order API matches this against a registered pickup
                      address by NAME, so it's chosen from the live list rather than typed —
                      a mismatch is the most common cause of a rejected order push. */}
                {form.provider === 'shiprocket' && editingId && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Pickup Location</label>
                    <div className="flex gap-2">
                      <select
                        value={pickupLocation}
                        onChange={(e) => handleSavePickupLocation(e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                      >
                        <option value="">
                          {pickupLocations.length ? 'Select a pickup location...' : 'Load locations to choose one...'}
                        </option>
                        {/* Keep a previously saved value selectable even before the
                              list loads, so opening this form can't silently clear it. */}
                        {pickupLocation && !pickupLocations.some((l) => l.name === pickupLocation) && (
                          <option value={pickupLocation}>{pickupLocation} (saved)</option>
                        )}
                        {pickupLocations.map((loc) => (
                          <option key={loc.id || loc.name} value={loc.name}>
                            {loc.name}
                            {loc.city ? ` — ${loc.city} ${loc.pincode}` : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={loadPickupLocations}
                        disabled={loadingPickups}
                        className="px-3 py-2 text-sm border rounded-lg border-input bg-card hover:bg-accent disabled:opacity-50 whitespace-nowrap"
                      >
                        {loadingPickups ? 'Loading...' : 'Load from Shiprocket'}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Must match a pickup address registered in Shiprocket exactly. This is the address <em>name</em>,
                      not its numeric ID.
                    </p>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Credentials are set as environment variables in Railway (
                  <code className="bg-muted px-1">SHIPGLOBAL_API_EMAIL</code> and{' '}
                  <code className="bg-muted px-1">SHIPGLOBAL_API_PASSWORD</code>), not stored here — ShipGlobal
                  authenticates with your dashboard password, which shouldn&apos;t live in the database.
                </p>

                {editingId && (
                  <div>
                    <button
                      type="button"
                      onClick={handleTestConnection}
                      disabled={testing}
                      className="px-4 py-2 text-sm border rounded-lg border-input bg-card hover:bg-accent disabled:opacity-50"
                    >
                      {testing ? 'Testing...' : 'Test Connection'}
                    </button>
                    {testResult && (
                      <div
                        className={`mt-2 p-3 text-sm ${
                          testResult.connected
                            ? 'bg-green-50 dark:bg-green-950/50 text-green-800 dark:text-green-300 border rounded-lg border-green-200 dark:border-green-900'
                            : 'bg-red-50 dark:bg-red-950/50 text-red-800 dark:text-red-300 border rounded-lg border-red-200 dark:border-red-900'
                        }`}
                      >
                        {testResult.connected ? (
                          <>
                            <p className="font-medium">Connected successfully.</p>
                            {testResult.supports_tracking === false && (
                              <p className="text-xs mt-1">
                                Note: this carrier&apos;s API has no tracking endpoint, so shipment status still has to
                                be updated by hand.
                              </p>
                            )}
                          </>
                        ) : (
                          testResult.reason
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Tracking URL Pattern</label>
              <input
                type="text"
                value={form.tracking_url_pattern}
                onChange={(e) => setForm({ ...form, tracking_url_pattern: e.target.value })}
                placeholder="https://www.example.com/track/{awb}"
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Copy a tracking link from this carrier&apos;s site and replace the tracking number with{' '}
                <code className="bg-muted px-1">{'{awb}'}</code>. Leave blank to paste a full tracking link on each
                shipment instead.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Logo</label>
              {form.logo_url ? (
                <div className="flex items-center gap-3 border rounded-lg border-border p-3">
                  <img src={form.logo_url} alt="Carrier logo" className="h-8 w-auto object-contain" />
                  <button
                    onClick={() => setForm({ ...form, logo_url: '' })}
                    className="text-sm text-red-600 dark:text-red-400 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <FileUpload
                  uploadType="carrier-logo"
                  accept="image/*"
                  multiple={false}
                  maxFiles={1}
                  onUploadComplete={(files) => {
                    const uploaded = (files || []).find((f) => f.uploaded && f.url);
                    if (uploaded) setForm((prev) => ({ ...prev, logo_url: uploaded.url }));
                  }}
                />
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.supports_domestic}
                  onChange={(e) => setForm({ ...form, supports_domestic: e.target.checked })}
                />
                Ships domestic (India)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.supports_international}
                  onChange={(e) => setForm({ ...form, supports_international: e.target.checked })}
                />
                Ships international
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Display Order</label>
                <input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>
              <label className="flex items-end gap-2 text-sm pb-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Active (available when creating shipments)
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Internal Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
              />
            </div>
          </div>

          <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex justify-end gap-2">
            <button
              onClick={handleCloseClick}
              disabled={saving}
              className="px-4 py-2 border rounded-lg border-input hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50"
            >
              {saving ? 'Saving...' : 'Save Partner'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ShippingPartnersPage;
